import { lookupPostcode, normalisePostcode } from "./postcodesApi";

// Postcode district outlines for the "Gaps by keyword" map.
// Source: github.com/missinglink/uk-postcode-polygons - one GeoJSON file per
// postcode area ("HD", "S"), one feature per district (properties.name = "HD3").
// Data © Wikipedia contributors, CC BY-SA 3.0 - the map must show that credit.
// Pinned to one commit so the files can't change under us.
const SOURCE = "https://cdn.jsdelivr.net/gh/missinglink/uk-postcode-polygons@660f1d1d54a0524731a0ec688d63a3d913508ca3/geojson/";
export const BOUNDARIES_CREDIT = 'Postcode boundaries &copy; <a href="https://en.wikipedia.org/wiki/List_of_postcode_districts_in_the_United_Kingdom">Wikipedia contributors</a>, CC BY-SA';

// Remembered until the page is reloaded.
const areaCache = new Map(); // "HD" -> Promise<{ "HD3": feature, ... }>
const pointCache = new Map(); // "S1 2AB" -> Promise<[lat, lng] | null>

function loadArea(area) {
  if (!areaCache.has(area)) {
    const load = fetch(`${SOURCE}${area}.geojson`)
      .then(res => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then(json => Object.fromEntries((json.features || []).map(f => [f.properties?.name, f])))
      .catch(e => {
        console.error(`Postcode boundaries for ${area} failed to load`, e);
        areaCache.delete(area); // try again next time
        return {};
      });
    areaCache.set(area, load);
  }
  return areaCache.get(area);
}

function loadPoint(postcode) {
  if (!pointCache.has(postcode)) {
    pointCache.set(postcode, lookupPostcode(postcode).then(r => (r && r.lat != null ? [r.lat, r.lng] : null)));
  }
  return pointCache.get(postcode);
}

// { "HD3": { feature }, "S1 2AB": { point: [lat, lng] }, ... }
// Districts get their outline; full postcodes (and districts with no outline)
// get a point instead. Postcodes that can't be placed are left out.
export async function loadPostcodeShapes(postcodes) {
  const shapes = {};
  await Promise.all([...new Set(postcodes)].map(async original => {
    const postcode = normalisePostcode(original);
    const isDistrict = !postcode.includes(" ");
    const area = (postcode.match(/^[A-Z]{1,2}/) || [])[0];
    if (isDistrict && area) {
      const districts = await loadArea(area);
      if (districts[postcode]) { shapes[original] = { feature: districts[postcode] }; return; }
    }
    const point = await loadPoint(postcode);
    if (point) shapes[original] = { point };
  }));
  return shapes;
}
